import {useContext, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {AdministrationApi} from "../../api/authority/generated/index.ts";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import Badge from "../../components/Badge.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import Tooltip from "../../components/Tooltip.jsx";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {SortableHeader, TablePagination} from "./ReportingLab.jsx";

const actions = [
    "trial_started", "subscription_requested", "subscription_activated", "subscription_changed",
    "subscription_change_scheduled", "cancellation_scheduled", "cancellation_cleared",
    "subscription_canceled", "renewal_succeeded", "renewal_failed",
];

function actionLabel(action, t) {
    const labels = {
        trial_started: t("admin.subscription_audit.action_trial_started", {defaultValue: "Zahájení zkoušky"}),
        subscription_requested: t("admin.subscription_audit.action_subscription_requested", {defaultValue: "Výběr placeného balíčku"}),
        subscription_activated: t("admin.subscription_audit.action_subscription_activated", {defaultValue: "Aktivace předplatného"}),
        subscription_changed: t("admin.subscription_audit.action_subscription_changed", {defaultValue: "Změna balíčku"}),
        subscription_change_scheduled: t("admin.subscription_audit.action_subscription_change_scheduled", {defaultValue: "Naplánovaná změna balíčku"}),
        cancellation_scheduled: t("admin.subscription_audit.action_cancellation_scheduled", {defaultValue: "Naplánované zrušení"}),
        cancellation_cleared: t("admin.subscription_audit.action_cancellation_cleared", {defaultValue: "Odvolané zrušení"}),
        subscription_canceled: t("admin.subscription_audit.action_subscription_canceled", {defaultValue: "Ukončení předplatného"}),
        renewal_succeeded: t("admin.subscription_audit.action_renewal_succeeded", {defaultValue: "Úspěšné obnovení"}),
        renewal_failed: t("admin.subscription_audit.action_renewal_failed", {defaultValue: "Neúspěšné obnovení"}),
    };
    return labels[action] ?? action;
}

function actionBadgeColor(action) {
    if (["renewal_succeeded", "subscription_activated", "cancellation_cleared"].includes(action)) return "emerald";
    if (["renewal_failed", "cancellation_scheduled", "subscription_canceled"].includes(action)) return "rose";
    if (["trial_started", "subscription_change_scheduled"].includes(action)) return "blue";
    return "violet";
}

function formatDate(value) {
    return value ? new Intl.DateTimeFormat("cs-CZ", {dateStyle: "medium", timeStyle: "medium", timeZone: "Europe/Prague"}).format(new Date(value)) : "—";
}

function formatNetPrice(amount, currency) {
    if (amount == null || !currency) return "—";

    return new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
    }).format(amount);
}

function CopyableIdentifier({label, value, t}) {
    if (!value) return null;

    const shortenedValue = `${value.slice(0, 8)}…${value.slice(-6)}`;

    async function copyValue() {
        try {
            await navigator.clipboard.writeText(value);
            toast.success(t("copy-to-clipboard-success"));
        } catch {
            toast.error(t("copy-to-clipboard-failed"));
        }
    }

    return <div className="mt-1 inline-flex items-center gap-1 whitespace-nowrap">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}:</span>
        <Tooltip bg="dark" size="md" position="bottom" className="inline-flex" trigger={<span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">{shortenedValue}</span>} onClick={() => void copyValue()}>
            <div className="break-all font-mono text-xs">{label}: {value}</div>
        </Tooltip>
    </div>;
}

export default function SubscriptionAudit() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {setCurrentProjectId} = useContext(GlobalContext);
    const configuration = useAuthorityConfiguration();
    const {et: translateError} = useErrorTranslation();
    const requestDependenciesRef = useRef({configuration, translateError, t});
    requestDependenciesRef.current = {configuration, translateError, t};
    const [data, setData] = useState({items: [], total: 0, sources: [], actors: []});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [action, setAction] = useState("");
    const [actorSelection, setActorSelection] = useState("");
    const [source, setSource] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sort, setSort] = useState({key: "occurredAt", direction: "desc"});

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [search]);

    useEffect(() => setPage(1), [debouncedSearch, action, actorSelection, source, pageSize]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const {configuration: currentConfiguration} = requestDependenciesRef.current;
        new AdministrationApi(currentConfiguration).listAdminSubscriptionAudit({
            page, pageSize,
            search: debouncedSearch || undefined,
            action: action || undefined,
            actorType: actorSelection === "system" ? "system" : actorSelection ? "user" : undefined,
            actorUserId: actorSelection && actorSelection !== "system" ? actorSelection : undefined,
            source: source || undefined,
            sortBy: sort.key,
            sortDirection: sort.direction,
        }).then((result) => {
            if (!cancelled) setData({items: result.items ?? [], total: result.total ?? 0, sources: result.sources ?? [], actors: result.actors ?? []});
        }).catch(async (error) => {
            const {translateError: translateCurrentError, t: translate} = requestDependenciesRef.current;
            if (!cancelled) toast.error(await translateCurrentError(error) || translate("admin.subscription_audit.load_failed", {defaultValue: "Audit předplatného se nepodařilo načíst."}));
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [page, pageSize, debouncedSearch, action, actorSelection, source, sort]);

    const sources = useMemo(() => Array.from(new Set([...(data.sources ?? []), source].filter(Boolean))).sort(), [data.sources, source]);
    const actors = useMemo(() => (data.actors ?? []).filter((actor) => actor.type === "user" && actor.userId), [data.actors]);
    const duplicateActorNames = useMemo(() => actors.reduce((names, actor) => {
        const name = actor.name || actor.email || actor.phone || "—";
        names.set(name, (names.get(name) ?? 0) + 1);
        return names;
    }, new Map()), [actors]);

    function actorOptionLabel(actor) {
        const name = actor.name || actor.email || actor.phone || "—";
        return duplicateActorNames.get(name) < 2 ? name : [name, actor.phone, actor.email].filter(Boolean).join(" · ");
    }

    function changeSort(sortKey) {
        setSort((current) => ({key: sortKey, direction: current.key === sortKey && current.direction === "desc" ? "asc" : "desc"}));
        setPage(1);
    }

    return <WideSecureLayout title={t("admin.subscription_audit.title", {defaultValue: "Audit předplatného"})}>
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_audit.description", {defaultValue: "Důkazní historie změn balíčků a předplatného. Záznamy jsou pouze pro čtení a nelze je zpětně upravit."})}</p>
            <section className="app-surface rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div className="grid gap-3 border-b border-slate-200 p-4 md:grid-cols-2 xl:grid-cols-4 dark:border-slate-700">
                    <input className="form-input w-full" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("admin.subscription_audit.search", {defaultValue: "Firma, projekt, uživatel nebo e-mail…"})}/>
                    <select className="form-select w-full" value={action} onChange={(event) => setAction(event.target.value)}>
                        <option value="">{t("admin.subscription_audit.all_actions", {defaultValue: "Všechny změny"})}</option>
                        {actions.map((value) => <option key={value} value={value}>{actionLabel(value, t)}</option>)}
                    </select>
                    <select className="form-select w-full" value={actorSelection} onChange={(event) => setActorSelection(event.target.value)}>
                        <option value="">{t("admin.subscription_audit.all_actors", {defaultValue: "Všichni původci"})}</option>
                        <option value="system">{t("admin.subscription_audit.actor_system", {defaultValue: "Systém"})}</option>
                        {actors.map((actor) => <option key={actor.userId} value={actor.userId}>{actorOptionLabel(actor)}</option>)}
                    </select>
                    <select className="form-select w-full" value={source} onChange={(event) => setSource(event.target.value)}>
                        <option value="">{t("admin.subscription_audit.all_sources", {defaultValue: "Všechny zdroje"})}</option>
                        {sources.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                </div>
                <div className="relative overflow-x-auto">
                    {loading && <LoadingComponent/>}
                    <table className="min-w-[1350px] w-full text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-900/50 dark:text-slate-400"><tr>
                            <SortableHeader className="px-4 py-3" label={t("admin.subscription_audit.change_time", {defaultValue: "Čas změny"})} sortKey="occurredAt" sort={sort} onSort={changeSort}/>
                            <SortableHeader className="px-4 py-3" label={t("admin.subscription_audit.project", {defaultValue: "Projekt"})} sortKey="project" sort={sort} onSort={changeSort}/>
                            <SortableHeader className="px-4 py-3" label={t("admin.subscription_audit.action", {defaultValue: "Změna"})} sortKey="action" sort={sort} onSort={changeSort}/>
                            <SortableHeader className="px-4 py-3" label={t("admin.subscription_audit.actor", {defaultValue: "Provedl"})} sortKey="actor" sort={sort} onSort={changeSort}/>
                            <SortableHeader className="px-4 py-3" label={t("admin.subscription_audit.package", {defaultValue: "Balíček"})} sortKey="currentPackage" sort={sort} onSort={changeSort}/>
                            <SortableHeader className="px-4 py-3" label={t("admin.subscription_audit.new_package", {defaultValue: "Nový balíček"})} sortKey="newPackage" sort={sort} onSort={changeSort}/>
                            <SortableHeader className="px-4 py-3" label={t("admin.subscription_audit.effective_at", {defaultValue: "Platí od"})} sortKey="effectiveAt" sort={sort} onSort={changeSort}/>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {(data.items ?? []).map((row) => <tr key={row.id} className="align-top odd:bg-white even:bg-slate-50/70 hover:bg-violet-50/70 dark:odd:bg-slate-800 dark:even:bg-slate-900/35 dark:hover:bg-violet-950/30">
                            <td className="whitespace-nowrap px-4 py-4">{formatDate(row.occurredAt)}</td>
                            <td className="px-4 py-4">
                                <button type="button" className="font-semibold text-slate-900 underline-offset-4 hover:text-violet-600 hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:text-white dark:hover:text-violet-300" onClick={() => {
                                    setCurrentProjectId(row.projectId);
                                    navigate("/");
                                }}>
                                    {row.companyName || row.projectName}
                                </button>
                                {row.companyName && <div className="text-xs text-slate-500 dark:text-slate-400">{row.projectName}</div>}
                                <CopyableIdentifier label="PID" value={row.projectId} t={t}/>
                            </td>
                            <td className="px-4 py-4"><Badge color={actionBadgeColor(row.action)}>{actionLabel(row.action, t)}</Badge></td>
                            <td className="px-4 py-4"><div className="font-semibold text-slate-900 dark:text-white">{row.actorType === "system" ? t("admin.subscription_audit.actor_system", {defaultValue: "Systém"}) : (row.actorName || row.actorEmail || row.actorPhone || "—")}</div>{row.actorType !== "system" && <div className="mt-1 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">{row.actorPhone && <div>{row.actorPhone}</div>}{row.actorEmail && <div>{row.actorEmail}</div>}</div>}</td>
                            <td className="px-4 py-4">{row.currentPackageName ? <><div className="font-semibold text-slate-900 dark:text-white">{row.currentPackageName}</div><div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{row.currentPackageNetPrice == null || !row.currentPackageCurrency ? "—" : <>{t("admin.subscription_audit.net_price", {defaultValue: "bez DPH"})} {formatNetPrice(row.currentPackageNetPrice, row.currentPackageCurrency)}</>}</div></> : "—"}</td>
                            <td className="px-4 py-4">{row.newPackageName ? <><div className="font-semibold text-slate-900 dark:text-white">{row.newPackageName}</div><CopyableIdentifier label="ID" value={row.newPackageId} t={t}/></> : "—"}</td>
                            <td className="whitespace-nowrap px-4 py-4">{formatDate(row.effectiveAt)}</td>
                        </tr>)}
                        {!loading && (data.items ?? []).length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">{t("admin.subscription_audit.empty", {defaultValue: "Filtrům neodpovídá žádná změna."})}</td></tr>}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 pb-4"><TablePagination page={page} pageSize={pageSize} totalItems={data.total ?? 0} onPageChange={setPage} onPageSizeChange={setPageSize} t={t}/></div>
            </section>
        </div>
    </WideSecureLayout>;
}
